/**
 * OriginBadge — where a fact, a value or a heading came from (New Draft §9.a).
 *
 * One vocabulary for the plan, the review and the editor, mirroring the
 * server's `app/core/option_lists.py::ORIGINS`:
 *
 *   verified · stated (user-supplied) · answered · extracted (from a
 *   document, quote found) · prefilled:<source> · inferred / assumption ·
 *   ai_proposed · placeholder · user (added by you) · required
 *
 * An inferred value is badged "Assumption" in the warning colour so it is
 * never read as a fact. The full sentence is the badge's title and its
 * screen-reader text.
 */
import { Badge, type BadgeVariant } from '@/components/ui/Badge';
import { cn } from '@/components/ui/cn';

export interface OriginInfo {
  label: string;
  description: string;
  variant: BadgeVariant;
}

const PLACEHOLDER: OriginInfo = {
  label: 'Placeholder',
  description: 'Left as a bracketed placeholder to be completed.',
  variant: 'default',
};

const ORIGINS: Record<string, OriginInfo> = {
  verified: { label: 'Verified', description: 'Checked against a record or source the system holds.', variant: 'success' },
  stated: { label: 'User-supplied', description: 'Entered or said by a person on this brief.', variant: 'info' },
  answered: { label: 'Answered', description: 'Given by a person in answer to a question.', variant: 'info' },
  extracted: {
    label: 'From document',
    description: 'Read from a reference document; the quote was found in its text.',
    variant: 'default',
  },
  prefilled: {
    label: 'Prefilled',
    description: 'Copied from a request, intake or template — not yet confirmed.',
    variant: 'default',
  },
  inferred: { label: 'Assumption', description: 'Inferred, not stated. Never treated as a fact.', variant: 'warning' },
  assumption: {
    label: 'Assumption',
    description: 'An assumption the plan makes until a person decides.',
    variant: 'warning',
  },
  ai_proposed: { label: 'AI-proposed', description: 'Proposed by the model for a person to review.', variant: 'accent' },
  placeholder: { label: 'Placeholder', description: 'Left as a bracketed placeholder to be completed.', variant: 'default' },
  user: { label: 'Added by you', description: 'Added by a person to the plan.', variant: 'info' },
  required: { label: 'Required', description: 'Required for this document type; added by the rules.', variant: 'default' },
};

const PREFILL_SOURCES: Record<string, string> = {
  intake: 'intake',
  request: 'contract request',
  contract_request: 'contract request',
  template: 'template',
  reference: 'reference document',
  document: 'reference document',
  client: 'client record',
  matter: 'matter',
};

/** The label, sentence and colour for an origin string. Pure. */
export function originInfo(origin: string | null | undefined): OriginInfo {
  if (!origin) return PLACEHOLDER;
  const [base = '', source] = origin.split(':', 2);
  const info = ORIGINS[base];
  if (!info) return { label: origin, description: origin, variant: 'default' };
  if (base === 'prefilled' && source) {
    const from = PREFILL_SOURCES[source] ?? source.replace(/_/g, ' ');
    return { ...info, label: `From ${from}`, description: `Copied from the ${from} — not yet confirmed.` };
  }
  return info;
}

export function OriginBadge({ origin, className }: { origin: string | null | undefined; className?: string }) {
  const info = originInfo(origin);
  return (
    <span className={cn('inline-flex', className)} title={info.description}>
      <Badge variant={info.variant}>
        {info.label}
        <span className="sr-only"> — {info.description}</span>
      </Badge>
    </span>
  );
}
